import type { MaybeArray, UnDef } from '@jhqn/utils'
import xss from 'xss'

export const resUrl = (url: UnDef<string>) => {
  if (!isDef(url) || !url) return undefined
  if (/^https?:\/\//.test(url) || url.startsWith('blob:') || url.startsWith('data:')) {
    return url
  }
  return `${import.meta.env.VITE_BASE_ATTACH_URL}${url.startsWith('/') ? url : `/${url}`}`.replace(/([^:]\/)\/+/g, '$1')
}

export const recoverFile = (resource: Partial<Upload.Resource>) => {
  const { uri, url, ...item } = resource
  const common = { ...item, uri: uri || url, url: resUrl(uri || url) }
  return { ...common, uid: uri, percent: 100, thumbUrl: resUrl(uri), status: 'done' } as Upload.Resource
}

/**
 * 附件格式
 * @param data
 */
export const attachFormat = (data: UnDef<MaybeArray<Upload.Resource>> | string) => {
  let attachList: Partial<Upload.Resource>[] = []
  // null 或 undefined
  if (!data) {
    attachList = []
  } else if (Array.isArray(data)) {
    // Upload.Resource[]
    attachList = data
  } else if (typeof data === 'string') {
    // 字符串
    // Upload.Resource[] string
    if (isArrStr(data)) {
      attachList = (parseToJSON<Upload.Resource[]>(data) || []).filter(Boolean)
    } else {
      const jsonData = parseToJSON<Upload.Resource | string>(data)
      // Upload.Resource string
      if (isObject(jsonData)) attachList = [jsonData as Upload.Resource].filter(Boolean)
      // 单个uri string
      else attachList = [{ id: data, name: data, uri: data, group: 'default' }]
    }
  } else if (isObject(data)) {
    // Upload.Resource
    attachList = [data]
  }
  return attachList.filter(item => !isEmpty(item)).map(item => recoverFile(item))
}

/**
 * rgb、rgba、red/green/yellow/blue 转16进制
 * @param color
 */
export const colorToHex = (color?: string) => {
  const colorStr: Record<string, string> = { red: '#ff0000', green: '#91d740', yellow: '#fcaa72', blue: '#43a5fe' }
  if (!color) return color
  if (color.includes('#')) return color
  if (color.includes('rgb')) {
    const values = color
      .replace(/rgba?\(/, '')
      .replace(/\)/, '')
      .replace(/[\s+]/g, '')
      .split(',')
    const a = Number.parseFloat(values[3] || '1')
    const r = Math.floor(a * Number.parseInt(values[0], 10) + (1 - a) * 255)
    const g = Math.floor(a * Number.parseInt(values[1], 10) + (1 - a) * 255)
    const b = Math.floor(a * Number.parseInt(values[2], 10) + (1 - a) * 255)
    return `#${`0${r.toString(16)}`.slice(-2)}${`0${g.toString(16)}`.slice(-2)}${`0${b.toString(16)}`.slice(-2)}`
  }
  return colorStr[color]
}

/**
 * 文件大小格式化
 * @param size
 */
export const sizeFmt = (size: number | bigint | undefined) => {
  if (size === undefined) return ''
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'unit',
    unit: 'byte',
    unitDisplay: 'narrow',
  }).format(size)
}

export const xssFn = (v: UnDef<string>) => {
  return xss((v || '-').replace(/\n/g, '<br>').replace(/label/g, 'span'))
}

/**
 * traverse array to tree
 * @param data
 * @param parentId
 */
export const arrayToTree = <T extends { id: string; parentId?: string; children: T[] | undefined }>(
  data: T[],
  parentId = '0'
) => {
  const result: T[] = []
  if (!isArray(data)) {
    return result
  }
  data.forEach(item => {
    if (item.parentId === parentId) {
      const children = arrayToTree(data, item.id)
      if (children.length) item.children = children
      result.push(item)
    }
  })
  return result
}

/**
 * traverse tree to array
 * @param data
 */
export const treeToArray = <
  T extends { id?: string; parentId?: string; children?: T[] | undefined; hidden?: boolean; sort?: number },
>(
  data: T[]
) => {
  const result: T[] = []
  if (!isArray(data)) return result
  data
    .sort((a, b) => a.sort! - b.sort!)
    .filter(item => !item.hidden)
    .forEach(item => {
      result.push(item)
      if (isArray(item.children)) {
        result.push(...treeToArray(item.children))
      }
    })
  return result
}

/**
 * 根据身份证号返回性别
 * @param idCard 身份证号
 * @returns 1-男 2-女 0-未知
 */
export const getSexByIDCard = (idCard?: string | null) => {
  let sex = 0
  if (idCard === undefined || idCard === null) return 0
  if (idCard.length === 18) {
    if (Number(idCard.charAt(16)) % 2 === 0) sex = 2
    else sex = 1
  }
  if (idCard.length === 15) {
    if (Number(idCard.charAt(14)) % 2 === 0) sex = 2
    else sex = 1
  }
  return sex
}

/**
 * 根据身份证号返回年龄
 * @param idCard 身份证号
 * @returns 年龄
 */
export const getAgeByIDCard = (idCard?: string | null) => {
  if (!idCard) return ''
  if (idCard.includes('*')) return '*'
  const thisDate = new Date()
  const year = thisDate.getFullYear()
  const month = thisDate.getMonth() + 1
  const day = thisDate.getDate()
  let tempYear = Number(idCard.substring(6, 10)) || 0
  let tempMonth = Number(idCard.substring(10, 12)) || 0
  let tempDay = Number(idCard.substring(12, 14)) || 0
  if (idCard.length === 15) {
    const org_day = idCard.substring(6, 12)
    tempYear = Number(`19${org_day.substring(0, 2)}`)
    tempMonth = Number(org_day.substring(2, 4))
    tempDay = Number(org_day.substring(4, 6))
  }
  let age = year - tempYear
  if (tempMonth > month || (tempMonth === month && tempDay > day)) age -= 1
  return age
}

/**
 * 根据身份证号返回性别和年龄
 * @param idCard
 */
export const getInfoByIDCard = (idCard?: string | null) => {
  const sex = getSexByIDCard(idCard)
  const age = getAgeByIDCard(idCard)
  return { sex, age }
}

export const addUnit = (value?: Numeric): string | undefined => {
  if (isDef(value)) {
    return isNumeric(value) ? `${value}px` : String(value)
  }
  return undefined
}

export const formattedNumber = (v: UnDef<number | string>, digits?: number): string | null => {
  if (isNumeric(v)) {
    const num = Number(v)
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(num)
  }
  return null
}

/**
 * 处理图片地址
 * @param html
 * @returns
 */
export const replaceImageUrl = (html?: string) => {
  const baseUrl = import.meta.env.VITE_BASE_ATTACH_URL
  return html?.replace(/@URL/gi, baseUrl) || ''
}
